import { cfApiClient } from '@/api/cf-worker-client';
import type { Category, CreateCategory, ApiResponse } from '@/types';

export interface CategoriesResponse {
  categories: Category[];
}

// Helper function to transform API response to Category type
const transformCategory = (apiCategory: any): Category => ({
  ...apiCategory,
  id: String(apiCategory.id),
  // Ensure boolean isActive from API is properly typed
  isActive: apiCategory.isActive === true || apiCategory.isActive === 1 || apiCategory.isActive === 'true',
  thumbnailUrl: apiCategory.thumbnailUrl || null,
});

// Helper function to transform CreateCategory to API format
const transformCreateCategory = (category: CreateCategory): Record<string, unknown> => ({
  name: category.name,
  slug: category.slug,
  description: category.description,
  thumbnailUrl: category.thumbnailUrl,
  isActive: category.isActive,
});

export const categoriesService = {
  getCategories: async (): Promise<CategoriesResponse> => {
    const response = await cfApiClient.get<ApiResponse<Category[]>>('/categories');
    return { categories: (response.data || []).map(transformCategory) };
  },

  getCategory: async (id: string): Promise<Category> => {
    const response = await cfApiClient.get<ApiResponse<Category>>(`/categories/${id}`);
    return transformCategory(response.data);
  },

  createCategory: async (category: CreateCategory): Promise<Category> => {
    const apiPayload = transformCreateCategory(category);
    const response = await cfApiClient.post<ApiResponse<Category>>('/categories', apiPayload);
    return transformCategory(response.data);
  },

  updateCategory: async (id: string, category: any): Promise<Category> => {
    // Map Category fields to backend expected fields for update (camelCase)
    const updateData = {
      name: category.name,
      slug: category.slug,
      description: category.description,
      thumbnailUrl: category.thumbnailUrl,
      isActive: category.isActive,
    };
    const response = await cfApiClient.put<ApiResponse<Category>>(`/categories/${id}`, updateData);
    return transformCategory(response.data);
  },

  deleteCategory: async (id: string): Promise<void> => {
    await cfApiClient.delete<ApiResponse<void>>(`/categories/${id}`);
  },
};
